import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

const CHOICE = { yes: "Yes / Affirmative", no: "No / Negative", abstain: "Abstain", absent: "Absent", not_applicable: "Did not vote" };

function Letterhead({ lgu, title }) {
  return (
    <header className="mb-6 text-center">
      <div className="text-xs uppercase tracking-widest">Republic of the Philippines</div>
      <div className="font-semibold uppercase">{lgu?.name}</div>
      <div className="text-sm uppercase">{lgu?.legislative_body_name}</div>
      <div className="text-xs">Office of the Secretary to the Sanggunian</div>
      <h1 className="mt-5 font-serif text-base font-bold uppercase tracking-wide">{title}</h1>
    </header>
  );
}

function SessionLine({ s }) {
  return (
    <table className="mb-5 w-full text-sm"><tbody>
      <tr><td className="w-40 py-0.5 align-top">Session</td><td>{label(s.session_type)} Session No. {s.session_no} — {s.title}</td></tr>
      <tr><td className="py-0.5">Date and time</td><td>{fmtDateTime(s.scheduled_at)} (Asia/Manila)</td></tr>
      <tr><td className="py-0.5">Venue</td><td>{s.venue || "—"}</td></tr>
      <tr><td className="py-0.5">Term</td><td>{s.term}</td></tr>
    </tbody></table>
  );
}

export function Sign({ caption, name, role, testId }) {
  return (
    <div className="break-inside-avoid" data-testid={testId}>
      <div className="mb-10 text-xs italic">{caption}</div>
      <div className="border-t border-black pt-1 text-center text-sm font-semibold uppercase">{name || "\u00a0"}</div>
      <div className="text-center text-xs">{role}</div>
      <div className="mt-4 text-xs">Date: ______________________</div>
    </div>
  );
}

function Toggle({ k, opts, setOpts, text }) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={!!opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} data-testid={`print-opt-${k}`} />{text}
    </label>
  );
}

function OrderOfBusiness({ s, lgu, opts }) {
  return (
    <div data-testid="print-order-of-business">
      <Letterhead lgu={lgu} title="Order of Business" />
      <SessionLine s={s} />
      <ol className="mb-8 space-y-2 text-sm">
        {s.agenda.map((a) => (
          <li key={a.id} className="flex gap-3" data-testid={`print-agenda-${a.sequence}`}>
            <span className="w-6 shrink-0 text-right">{a.sequence}.</span>
            <div><div>{a.title}</div><div className="text-xs italic">{label(a.item_type)}</div></div>
          </li>
        ))}
        {s.agenda.length === 0 && <li className="italic">No agenda items recorded.</li>}
      </ol>
      {opts.order_adoption_field && (
        <div className="mb-8 break-inside-avoid border border-black p-3 text-sm" data-testid="print-adoption-block">
          <div className="mb-2 font-semibold uppercase">Adoption of the Order of Business</div>
          <p>Adopted / approved by the {lgu?.legislative_body_name || "Sanggunian"} during the session held on ____________________,
            upon motion of Hon. ______________________________, duly seconded by Hon. ______________________________.</p>
          <p className="mt-2">Motion / Resolution No.: ____________ &nbsp; Vote: ______ in favor, ______ against, ______ abstaining.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-12">
        <Sign caption="Prepared by:" name={s.secretary} role="Secretary to the Sanggunian" testId="sign-secretary" />
        {opts.order_attestation && <Sign caption="Attested / approved:" name={s.presiding} role="Presiding Officer" testId="sign-presiding" />}
      </div>
    </div>
  );
}

function RollCall({ s, v, lgu, opts }) {
  const draft = v.status !== "finalized";
  return (
    <div className="mb-12" style={{ breakAfter: "page" }} data-testid={`print-rollcall-${v.id}`}>
      <Letterhead lgu={lgu} title="Roll-Call Result Sheet" />
      {draft && <div className="mb-4 border border-black p-2 text-center text-xs font-semibold uppercase">Vote still open — not a certified result</div>}
      <SessionLine s={s} />
      <table className="mb-4 w-full text-sm"><tbody>
        <tr><td className="w-40 py-0.5 align-top">Question</td><td className="font-semibold">{v.question}</td></tr>
        <tr><td className="py-0.5">Method / rule</td><td>{label(v.method)} · {label(v.majority_rule)} majority</td></tr>
        <tr><td className="py-0.5">Quorum</td><td>{v.present_count ?? s.quorum.present} present of {s.quorum.members} members; {v.quorum_required ?? s.quorum.required} required</td></tr>
      </tbody></table>
      <table className="mb-4 w-full border-collapse text-sm">
        <thead><tr><th className="border border-black px-2 py-1 text-left">#</th><th className="border border-black px-2 py-1 text-left">Member</th><th className="border border-black px-2 py-1 text-left">Vote</th></tr></thead>
        <tbody>
          {s.members.map((m, i) => {
            const r = v.records.find((x) => x.legislator_id === m.id);
            return <tr key={m.id}><td className="border border-black px-2 py-1">{i + 1}</td><td className="border border-black px-2 py-1">Hon. {m.name}</td>
              <td className="border border-black px-2 py-1" data-testid={`print-ballot-${v.id}-${m.id}`}>{r ? CHOICE[r.choice] : "—"}</td></tr>;
          })}
        </tbody>
      </table>
      <div className="mb-3 text-sm" data-testid={`print-tally-${v.id}`}>
        Yes: <b>{v.yes_count}</b> · No: <b>{v.no_count}</b> · Abstain: <b>{v.abstain_count}</b> · Absent: <b>{v.absent_count}</b>
      </div>
      <div className="mb-3 text-sm">Result: <b className="uppercase">{v.declared_outcome || "pending"}</b>{v.computed_outcome && v.computed_outcome !== v.declared_outcome && ` (computed: ${v.computed_outcome})`}
        {v.declaration_remarks && <div className="text-xs italic">Remarks: {v.declaration_remarks}</div>}</div>
      {v.corrections.length > 0 && (
        <div className="mb-4 text-xs"><div className="font-semibold">Recorded corrections</div>
          {v.corrections.map((c) => <div key={c.id}>{c.name}: {CHOICE[c.old_choice]} → {CHOICE[c.new_choice]} — {c.reason} ({fmtDateTime(c.created_at)})</div>)}</div>
      )}
      <div className="mt-8 grid grid-cols-2 gap-12">
        <Sign caption="Recorded and certified correct:" name={s.secretary} role="Secretary to the Sanggunian" testId={`sign-certify-${v.id}`} />
        {opts.rollcall_confirmation && <Sign caption="Confirmed:" name={s.presiding} role="Presiding Officer" testId={`sign-confirm-${v.id}`} />}
      </div>
      <footer className="mt-6 border-t pt-1 font-mono text-[10px] text-gray-500">Vote ID {v.id} · finalized {v.finalized_at ? fmtDateTime(v.finalized_at) : "—"} · Generated by OTMS {fmtDateTime(new Date().toISOString())}</footer>
    </div>
  );
}

export default function SessionPrint() {
  const { id, kind } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [opts, setOpts] = useState({});
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/sessions/${id}`).then((r) => setS(r.data)).catch((e) => setError(errMsg(e)));
    api.get("/post-approval/settings").then((r) => setOpts(r.data));
  }, [id]);
  if (error) return <div className="p-8 text-sm text-red-700" data-testid="session-print-error">{error}</div>;
  if (!s) return <div className="p-8 text-sm">Loading…</div>;
  const voteId = params.get("vote");
  const votes = s.votes.filter((v) => (voteId ? v.id === voteId : v.status === "finalized"));
  return (
    <div className="mx-auto max-w-3xl bg-white p-10 font-serif text-[15px] leading-relaxed text-black" data-testid="session-print-view">
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 p-3 font-sans text-xs">
        <div className="space-y-1">
          {kind === "order-of-business" ? (<>
            <Toggle k="order_attestation" opts={opts} setOpts={setOpts} text="Include Presiding Officer attestation" />
            <Toggle k="order_adoption_field" opts={opts} setOpts={setOpts} text="Include adoption / approval field" />
          </>) : <Toggle k="rollcall_confirmation" opts={opts} setOpts={setOpts} text="Include Presiding Officer confirmation" />}
        </div>
        <Button onClick={() => window.print()} data-testid="print-now-button">Print / Save as PDF</Button>
      </div>
      {kind === "order-of-business" ? <OrderOfBusiness s={s} lgu={user.lgu} opts={opts} />
        : votes.length ? votes.map((v) => <RollCall key={v.id} s={s} v={v} lgu={user.lgu} opts={opts} />)
          : <div className="text-sm italic" data-testid="print-no-votes">No finalized roll-call votes in this session.</div>}
    </div>
  );
}
